/**
 * Public marketing landing.
 *
 * Hero is an asymmetric grid (text left + product dashboard right
 * with the whale mascot peeking from the corner). ProductTour shows
 * each surface inside a faux Telegram WebApp chrome so visitors
 * read "this is the actual app", not a generic dashboard preview.
 */
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import {
  ActivityIcon,
  CheckIcon,
  WalletIcon,
} from '../components/ui';
import {
  Aurora,
  BOT_URL,
  BtcChart,
  DotGrid,
  HeroEyebrow,
  ProductDashboardMock,
  Reveal,
  SectionHeading,
} from './shared';
import {
  SectionCta,
  SectionFaq,
  SectionFeatures,
  SectionFees,
  SectionHow,
  SectionSecurity,
} from './sections';
import { useDocumentMeta } from './useDocumentMeta';

export function LandingProduct() {
  const { t } = useTranslation();
  // Re-apply landing meta when the user navigates here from /legal/*
  // — the hook in SiteLegal swaps title/canonical on mount; on
  // unmount it restores the previous values, but the user might
  // open /legal directly first (no previous landing value to
  // restore to). Setting it explicitly here keeps the tab title
  // honest regardless of entry point.
  useDocumentMeta({
    title: `Kit Pod — ${t('site.hero.eyebrow')}`,
    description: t('site.hero.subtitle'),
    canonical: '/',
  });
  return (
    <>
      <Hero />
      <SectionFeatures />
      <ProductTour />
      <SectionHow />
      <SectionSecurity />
      <SectionFees />
      <SectionFaq />
      <SectionCta />
    </>
  );
}

/* ── Hero — asymmetric, text left + dashboard right ───────────── */

function Hero() {
  const { t } = useTranslation();
  const chips = [
    'chip_control',
    'chip_adapts',
    'chip_fee_when_profit',
  ] as const;
  return (
    <section className="relative overflow-hidden">
      <Aurora variant="wide" />
      <DotGrid />
      <div className="relative mx-auto grid max-w-6xl gap-12 px-5 pb-20 pt-16 sm:px-6 lg:grid-cols-[1.05fr_0.95fr] lg:items-center lg:gap-16 lg:pb-28 lg:pt-24">
        <div className="pb-fade-up">
          <HeroEyebrow>{t('site.hero.eyebrow')}</HeroEyebrow>
          <h1 className="pb-display mt-6 text-[clamp(38px,5.8vw,64px)] leading-[1.02]">
            {t('site.hero.title')}
          </h1>
          <p className="mt-6 max-w-xl text-[17px] leading-relaxed text-tg-hint sm:text-[18px]">
            {t('site.hero.subtitle')}
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <a
              href={BOT_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="pb-btn pb-btn-primary pb-focus-ring px-7 text-[15px]"
            >
              {t('site.hero.cta')}
            </a>
            <a
              href="#how"
              className="pb-btn pb-btn-secondary pb-focus-ring px-7 text-[15px]"
            >
              {t('site.hero.cta_secondary')}
            </a>
          </div>
          <ul className="mt-8 flex flex-wrap gap-x-6 gap-y-3">
            {chips.map((c) => (
              <li
                key={c}
                className="inline-flex items-center gap-2 text-[13px] text-tg-text/90"
              >
                <CheckIcon size={14} className="text-pb-accent" />
                {t(`site.hero.${c}`)}
              </li>
            ))}
          </ul>
        </div>
        <div className="mx-auto w-full max-w-[480px]">
          {/* Mascot anchored to the dashboard box (NOT the column) so
              it lands on the corner instead of floating in empty
              column space. Hidden on phones where the stacked
              dashboard already fills the viewport. */}
          <div className="site-float relative">
            <ProductDashboardMock />
            <img
              src="/whale-mascot.webp"
              alt=""
              aria-hidden
              width={512}
              height={341}
              className="pointer-events-none absolute -right-10 -top-16 hidden h-auto w-40 drop-shadow-2xl sm:block lg:-right-16 lg:-top-20 lg:w-52"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── Product tour — chrome'd Telegram WebApp cards ─────────────── */

const TOUR_CARDS = [
  { key: 'card1', visual: 'trade' as const, title: 'Kit Pod · Dashboard' },
  { key: 'card2', visual: 'history' as const, title: 'Kit Pod · Activity' },
  { key: 'card3', visual: 'wallet' as const, title: 'Kit Pod · Wallet' },
];

function ProductTour() {
  const { t } = useTranslation();
  return (
    <section className="relative overflow-hidden border-t border-pb-border-soft bg-pb-paper/40">
      <Aurora variant="subtle" />
      <div className="relative mx-auto max-w-6xl px-5 py-20 sm:px-6 lg:py-28">
        <Reveal>
          <SectionHeading
            eyebrow={t('site.product_tour.eyebrow')}
            title={t('site.product_tour.title')}
          />
        </Reveal>
        <div className="mt-14 grid gap-6 lg:grid-cols-3">
          {TOUR_CARDS.map(({ key, visual, title }, i) => (
            <Reveal key={key} delay={i * 100}>
              <article className="flex h-full flex-col overflow-hidden rounded-pb-xl border border-pb-border-soft bg-pb-paper shadow-pb-md">
                <TgChrome title={title}>
                  <TourVisual kind={visual} />
                </TgChrome>
                <div className="p-6">
                  <h3 className="text-[17px] font-semibold">
                    {t(`site.product_tour.${key}_title`)}
                  </h3>
                  <p className="mt-2 text-[14px] leading-relaxed text-tg-hint">
                    {t(`site.product_tour.${key}_body`)}
                  </p>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/**
 * Faux Telegram WebApp chrome wrapper — iOS-style status bar (time +
 * battery) over a Telegram nav (back arrow + screen title + dots).
 * Used to wrap product visuals so they read as actual app screens.
 */
function TgChrome({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="relative border-b border-pb-border-soft bg-gradient-to-b from-[#1f2937]/40 to-pb-paper2/50 p-3 sm:p-4">
      {/* iOS-style status bar */}
      <div className="flex items-center justify-between px-1 text-[10px] font-medium text-tg-text/70">
        <span className="font-mono">9:41</span>
        <div className="flex items-center gap-1">
          <SignalGlyph />
          <span className="font-mono">5G</span>
          <BatteryGlyph />
        </div>
      </div>
      {/* Telegram-style nav */}
      <div className="mt-2 flex items-center gap-2 border-b border-white/[0.05] pb-2">
        <ChevronLeftGlyph />
        <span className="truncate text-[11px] font-medium text-tg-text/80">
          {title}
        </span>
        <span className="ml-auto inline-flex items-center gap-1 text-tg-hint">
          <span className="h-1 w-1 rounded-full bg-current" />
          <span className="h-1 w-1 rounded-full bg-current" />
          <span className="h-1 w-1 rounded-full bg-current" />
        </span>
      </div>
      {/* Screen contents — min-height aligns all three tour cards so
          the descriptive text under each chrome line lands at the
          same y across columns. Mobile only stacks single column so
          alignment doesn't matter; trim the floor there. */}
      <div className="mt-3 sm:min-h-[320px]">{children}</div>
    </div>
  );
}

function SignalGlyph() {
  return (
    <svg width="12" height="9" viewBox="0 0 12 9" aria-hidden fill="currentColor">
      <rect x="0" y="6" width="2" height="3" rx="0.4" />
      <rect x="3" y="4" width="2" height="5" rx="0.4" />
      <rect x="6" y="2" width="2" height="7" rx="0.4" />
      <rect x="9" y="0" width="2" height="9" rx="0.4" opacity="0.5" />
    </svg>
  );
}

function BatteryGlyph() {
  return (
    <svg width="18" height="9" viewBox="0 0 18 9" aria-hidden fill="none">
      <rect x="0.5" y="0.5" width="14" height="8" rx="1.5" stroke="currentColor" strokeWidth="0.8" />
      <rect x="2" y="2" width="9" height="5" rx="0.5" fill="currentColor" />
      <rect x="15" y="3" width="2" height="3" rx="0.4" fill="currentColor" />
    </svg>
  );
}

function ChevronLeftGlyph() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden fill="none">
      <path d="M9 3L5 7l4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function TourVisual({ kind }: { kind: 'trade' | 'history' | 'wallet' }) {
  const { t } = useTranslation();
  if (kind === 'trade') {
    return (
      <div className="rounded-pb-md border border-white/[0.06] bg-pb-paper2 p-4">
        <div className="flex items-center justify-between">
          <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-wider text-pb-success">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-pb-success" />
            {t('site.panel.active')}
          </span>
          <span className="font-mono text-[10px] text-tg-hint">ETH · 5м</span>
        </div>
        <div className="mt-3 h-24">
          <BtcChart />
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2">
          <div className="rounded-pb-sm border border-pb-accent-line bg-pb-accent-soft p-2 text-center">
            <p className="pb-eyebrow !text-tg-hint">
              {t('site.panel.strategy')}
            </p>
            <p className="pb-numeral mt-0.5 text-[12px] font-medium text-pb-accent">
              {t('site.panel.strategy_value')}
            </p>
          </div>
          <div className="rounded-pb-sm border border-[color-mix(in_oklab,var(--pb-success)_36%,transparent)] bg-pb-success-soft p-2 text-center">
            <p className="pb-eyebrow !text-tg-hint">
              {t('site.panel.signal')}
            </p>
            <p className="pb-numeral mt-0.5 text-[12px] font-medium text-pb-success">
              ↑ {t('site.panel.signal_value')}
            </p>
          </div>
        </div>
        <div className="mt-3 flex items-center gap-2 rounded-pb-sm bg-white/[0.02] px-3 py-2 text-[11px]">
          <span className="pb-eyebrow !text-tg-hint">{t('site.panel.bot')}</span>
          <span className="ml-auto text-pb-success">
            {t('site.panel.active_status')}
          </span>
          <span
            aria-hidden
            className="inline-flex h-3.5 w-6 items-center rounded-full bg-pb-success p-0.5"
          >
            <span className="ml-auto h-2.5 w-2.5 rounded-full bg-white" />
          </span>
        </div>
      </div>
    );
  }
  if (kind === 'history') {
    const rows: { time: string; dir: '↑' | '↓'; amount: string; tone: 'success' | 'danger' }[] = [
      { time: '14:32', dir: '↑', amount: '+$2.40', tone: 'success' },
      { time: '14:18', dir: '↑', amount: '+$1.10', tone: 'success' },
      { time: '14:03', dir: '↓', amount: '−$0.80', tone: 'danger' },
      { time: '13:48', dir: '↑', amount: '+$0.65', tone: 'success' },
      { time: '13:33', dir: '↑', amount: '+$2.05', tone: 'success' },
      { time: '13:18', dir: '↑', amount: '+$1.40', tone: 'success' },
    ];
    return (
      <div className="rounded-pb-md border border-white/[0.06] bg-pb-paper2 p-4">
        <div className="flex items-center justify-between">
          <span className="pb-eyebrow">{t('site.panel.today')}</span>
          <span className="pb-numeral font-mono text-[14px] font-semibold text-pb-success">
            +$13.80
          </span>
        </div>
        <ul className="mt-3 space-y-1">
          {rows.map((r) => (
            <li
              key={r.time}
              className="flex items-center justify-between rounded-pb-sm bg-white/[0.02] px-3 py-1.5 text-[11px]"
            >
              <span className="flex items-center gap-2">
                <span
                  className={clsx(
                    'flex h-4 w-4 items-center justify-center rounded-full text-[9px]',
                    r.tone === 'success'
                      ? 'bg-pb-success-soft text-pb-success'
                      : 'bg-pb-danger/[0.15] text-pb-danger',
                  )}
                  aria-hidden
                >
                  {r.dir}
                </span>
                <span className="font-mono text-tg-hint">{r.time}</span>
              </span>
              <span
                className={
                  r.tone === 'success'
                    ? 'pb-numeral font-medium text-pb-success'
                    : 'pb-numeral font-medium text-pb-danger'
                }
              >
                {r.amount}
              </span>
            </li>
          ))}
        </ul>
      </div>
    );
  }
  return (
    <div className="rounded-pb-md border border-white/[0.06] bg-pb-paper2 p-4">
      <div className="flex items-center gap-2">
        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-pb-accent-soft text-pb-accent">
          <WalletIcon size={20} />
        </span>
        <div className="min-w-0">
          <p className="pb-eyebrow !text-tg-hint">{t('site.panel.total')}</p>
          <p className="pb-numeral-lg text-[22px] leading-none">$1,240.00</p>
        </div>
      </div>
      <div className="mt-4 grid grid-cols-3 gap-2">
        <ActionTile label="+" caption="USDT" />
        <ActionTile label="↑" caption="Bot" tone="accent" />
        <ActionTile label="↗" caption="Out" />
      </div>
      <div className="mt-3 space-y-1">
        <div className="flex items-center gap-2 rounded-pb-sm bg-white/[0.02] px-3 py-2 text-[11px]">
          <ActivityIcon size={12} className="text-tg-hint" />
          <span className="font-mono text-tg-hint">14:31</span>
          <span className="text-tg-text">{t('site.panel.bot')} · USDT</span>
          <span className="ml-auto text-pb-success">+$5.00</span>
        </div>
        <div className="flex items-center gap-2 rounded-pb-sm bg-white/[0.02] px-3 py-2 text-[11px]">
          <ActivityIcon size={12} className="text-tg-hint" />
          <span className="font-mono text-tg-hint">12:08</span>
          <span className="text-tg-text">USDT · Deposit</span>
          <span className="ml-auto text-pb-success">+$200.00</span>
        </div>
      </div>
    </div>
  );
}

function ActionTile({
  label,
  caption,
  tone,
}: {
  label: string;
  caption: string;
  tone?: 'accent';
}) {
  return (
    <div
      className={
        tone === 'accent'
          ? 'rounded-pb-sm border border-pb-accent-line bg-pb-accent-soft py-2 text-center'
          : 'rounded-pb-sm border border-white/[0.06] bg-pb-paper py-2 text-center'
      }
    >
      <p
        className={
          tone === 'accent'
            ? 'text-[14px] font-semibold leading-none text-pb-accent'
            : 'text-[14px] font-semibold leading-none'
        }
      >
        {label}
      </p>
      <p className="pb-eyebrow mt-1 !text-[9px] !text-tg-hint">{caption}</p>
    </div>
  );
}
